<script setup lang="ts">
import { chatMessagesApi } from '@/api/chat-messages';
import { chatsApi } from '@/api/chats';
import { useActionCable } from '@/hooks/actioncable';
import { convertKeys } from '@/utils/case-converter';
import { format } from 'date-fns';
import { groupBy, sortBy } from 'lodash';
import { computed, inject, nextTick, onMounted, ref } from 'vue';
import { userPictureUrl } from '@/utils/user-picture-url';

interface Props {
  productBooking: ProductBooking,
  chat: Chat,
}

const props = defineProps<Props>();

const user = inject<User>('user');

const userType = ref(user?.clientAccountId === props.productBooking.clientAccount.id ? 'client' : 'partner');
const otherUserType = ref(user?.clientAccountId === props.productBooking.clientAccount.id ? 'partner' : 'client');

const myLastReadMessageId = ref(userType.value === 'client' ?
  props.chat.clientLastReadMessageId :
  props.chat.partnerLastReadMessageId);

const otherLastReadMessageId = ref(userType.value === 'client' ?
  props.chat.partnerLastReadMessageId :
  props.chat.clientLastReadMessageId);

const recipient = ref(userType.value === 'client' ?
  props.productBooking.partnerAccount : props.productBooking.clientAccount);

const message = ref();
const messageInputElement = ref<HTMLElement>();

function sendMessage() {
  if (!messageInputElement.value) return;

  message.value = messageInputElement.value?.innerText.trim();

  if (!!message.value) {
    chatMessagesApi.create({
      chatId: props.productBooking.chatId,
      userId: user?.id,
      content: message.value,
    });
    message.value = '';

    messageInputElement.value.innerText = '';
  }
}

const newMessages = ref<ChatMessage[]>([]);
const messagesDiv = ref<HTMLDivElement>();

useActionCable(
  { channel: 'ChatChannel', id: props.productBooking.chatId },
  {
    received(eventNewResource:Record<string, unknown>) {
      if (eventNewResource.type === 'message') {
        const newMessage = convertKeys(eventNewResource.payload as Record<string, unknown>, 'camelize');
        newMessages.value.push(newMessage as unknown as ChatMessage);
        nextTick(() => {
          if (messagesDiv.value) {
            messagesDiv.value.scrollTop = messagesDiv.value.scrollHeight;
          }
        });
      } else if (eventNewResource.type === `${otherUserType.value}_last_read_message_id`) {
        otherLastReadMessageId.value = eventNewResource.payload as number;
      }
    },
  },
);

const allMessages = computed(() => (props.chat.chatMessages ?
  props.chat.chatMessages.concat(newMessages.value) :
  newMessages.value));

const sortedMessages = computed(() => groupBy(
  sortBy(allMessages.value, 'createdAt'),
  (msg) => format(new Date(msg.createdAt), 'yyyy-MM-dd')));

function handleBack() {
  window.history.back();
}

function updateRead() {
  const updateData = userType.value === 'client' ?
    { clientLastReadMessageId: myLastReadMessageId.value } :
    { partnerLastReadMessageId: myLastReadMessageId.value };

  chatsApi.update(props.chat.id, updateData);
}

const updateReadPending = ref(false);

function handleScroll() {
  if (!messagesDiv.value) return;

  // eslint-disable-next-line max-len, vue/max-len
  const messageDivIsAtTheEnd = messagesDiv.value.scrollTop + messagesDiv.value.offsetHeight === messagesDiv.value.scrollHeight;

  if (!messageDivIsAtTheEnd) return;

  const lastMessage = allMessages.value[allMessages.value.length - 1];
  if (lastMessage.userId !== user?.id && myLastReadMessageId.value !== lastMessage.id) {
    myLastReadMessageId.value = lastMessage.id;
    if (document.hidden) {
      updateReadPending.value = true;
    } else {
      updateRead();
    }
  }
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden && updateReadPending.value) {
    updateReadPending.value = false;
    updateRead();
  }
});

onMounted(() => {
  if (messagesDiv.value) {
    messagesDiv.value.scrollTop = messagesDiv.value.scrollHeight;
  }
});
</script>
<template>
  <the-layout
    hide-navbar
    :active-menu="2"
  >
    <div class="relative flex h-[calc(100dvh-64px)] flex-col justify-between bg-white md:mx-auto md:my-6 md:h-[calc(100vh-72px-24px-24px)] md:max-w-md md:rounded">
      <div
        class="sticky top-0 z-10 flex w-full shrink-0 flex-col border-b border-z-gray-100 bg-white px-6 py-5"
      >
        <div class="flex items-center justify-between">
          <div class="flex shrink-0 space-x-2">
            <button @click="handleBack">
              <img src="@/assets/icons/chevron-left-alt.svg">
            </button>
            <img
              :src="userPictureUrl(recipient.pictureUrl)"
              class="h-6 rounded-full"
            >
            <p class="line-clamp-1 font-medium text-z-gray-900">
              {{ recipient.firstName }}
            </p>
          </div>
          <p class="line-clamp-1 text-sm font-light text-z-gray-500">
            {{ productBooking.product.name }}
          </p>
        </div>
        <p class="mt-1 text-end text-sm font-light text-z-gray-500">
          {{ format(new Date(productBooking.startDate), "d 'de' MMM") }}
          -
          {{ format(new Date(productBooking.endDate), "d 'de' MMM") }}
        </p>
      </div>
      <div
        ref="messagesDiv"
        class="h-max grow flex-col overflow-y-scroll bg-white px-6 pb-6"
        @scrollend.prevent="handleScroll"
      >
        <div
          v-for="group, key in sortedMessages"
          :key="key"
          class="mt-4 flex flex-col"
        >
          <p class="mb-4 w-full text-center text-xs">
            {{ format(new Date(`${key} GMT-0300`), "d 'de' MMM") }}
          </p>
          <div class="flex w-full flex-col space-y-1">
            <template
              v-for="msg in group"
              :key="msg.id"
            >
              <div
                class="relative w-64 break-words rounded-lg p-3 pb-4 text-sm"
                :class="msg.userId === recipient.userId ?
                  'bg-z-gray-100 text-z-gray-900 self-start' :
                  'bg-z-gray-900 text-z-gray-100 self-end'"
              >
                <p>
                  {{ msg.content }}
                </p>
                <p class="absolute bottom-1 right-2 text-xs opacity-60">
                  {{ format(msg.createdAt, 'HH:mm') }}
                </p>
              </div>
              <div
                v-if="otherLastReadMessageId === msg.id &&
                  otherLastReadMessageId === allMessages[allMessages.length - 1].id"
                class="self-end text-xs text-z-gray-400"
              >
                Visto
              </div>
            </template>
          </div>
        </div>
      </div>
      <div class="h-fit min-h-[70px] w-full shrink-0 border-t border-z-gray-100 bg-white px-6 py-3 md:max-w-md">
        <div class="flex justify-between rounded-lg border border-z-gray-100 px-3 py-2">
          <div
            ref="messageInputElement"
            class="w-full max-w-[calc(100%-32px)] grow-0 cursor-text items-center text-wrap break-words text-sm text-z-gray-800 outline-none"
            contenteditable
            @keydown.enter.stop.prevent="sendMessage"
          />
          <button
            class="flex h-7 w-7 shrink-0 items-center justify-center self-end rounded-full bg-z-turquoise-600"
            @click="sendMessage"
          >
            <img
              src="@/assets/icons/send.svg"
              class="h-4 w-4"
            >
          </button>
        </div>
      </div>
    </div>
  </the-layout>
</template>
<style lang="css" scoped>
  div[contenteditable]:empty:before {
    content: 'Escribe un mensaje...';
    color: #9f9f9f;
  }
</style>
